import Bugsnag from '@bugsnag/js';
import { Auth } from 'aws-amplify';
import { parseParams } from '../utils';
import { LOCAL_AUTH_SHIM, localAuthentication } from '../utils/authShim';
import config from '../config';

const API_URL = config.app.mhubApiUrl;
const LOG_API_URL = config.api.logApiUrl;

export async function getOpportunityActivityLogs(opportunityId, { page = 1, perPage = 20 } = {}) {
  const url = parseParams(`${LOG_API_URL}/api/v1/activity-logs`, {
    entity_type: 'opportunity',
    entity_id: opportunityId,
    per_page: perPage,
    page,
  });
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    mode: 'cors',
  });
  if (!res.ok) {
    const message = await res.text();
    Bugsnag.notify(`Failed to GET ${url}: ${message}`);
    throw new Error(message || 'Failed to fetch activity logs');
  }
  return res.json();
}

// The 403 body from /authenticated/ is plain text today; tolerate a JSON {message} too.
function parseForbiddenMessage(body) {
  const text = (body || '').trim();
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.message === 'string') {
      return parsed.message.trim();
    }
  } catch (e) {
    // not JSON — plain-text body
  }
  return text;
}

// TODO: Temporary changes for LMS-2457
// When merge with sign-in consolidate branch, use the changes in there
export async function getAuthentication(query = {}) {
  // LOCAL-DEV ONLY: /authenticated/ is cookie-authenticated against the mhub monolith, which has no
  // local stack — offline it always returns {} and the app redirects to the login host. Serve the
  // fixture instead. No-op unless REACT_APP_LOCAL_AUTH_SHIM=1.
  if (LOCAL_AUTH_SHIM) {
    return localAuthentication();
  }

  const res = await fetch(parseParams(`${API_URL}/authenticated/`, query), {
    credentials: 'include',
    mode: 'cors',
  });
  if (!res.ok) {
    const message = await res.text();
    if (res.status === 403) {
      // LMS-5174: mhub-api answers 403 when the user's organisation has been suspended in
      // Subscription Management (the body is the plain-text reason). The session itself is
      // still valid, so this must not be folded into the "not logged in" empty result below:
      // the Auth provider would hand off to the login app, which bounces straight back here.
      return { forbidden: true, message: parseForbiddenMessage(message) };
    }
    Bugsnag.notify(`Failed to GET ${API_URL}/authenticated/: ${message}`);
    return {};
  }

  const data = await res.json();
  if (!(data.affiliate && data.affiliate.company && data.affiliate.company.apps)) {
    return {};
  }

  return data || {};
}

export async function getProjects(bookApiUrl, search = null, limit = 1500) {
  const queries = [];
  if (limit > 0) {
    queries.push(`limit=${limit}`);
  }
  if (search) {
    queries.push(`search=${search}`);
  }
  const res = await fetch(`${bookApiUrl}/projects/?${queries.join('&')}`, {
    credentials: 'include',
    mode: 'cors',
  });
  const d = await res.json();
  return d;
}

export async function getProjectInfo(
  bookApiUrl,
  projectID,
  getRawResponse,
) {
  const res = await fetch(`${bookApiUrl}/projects/${projectID}/`, {
    credentials: 'include',
    mode: 'cors',
  });

  if (getRawResponse) {
    return res;
  }
  const d = await res.json();
  return d || {};
}

export async function getProjectUnits(
  bookApiUrl,
  projectID,
  unitStatuses = null,
  search = null,
  limit = 2000,
) {
  const queries = [];
  if (limit > 0) {
    queries.push(`limit=${limit}`);
  }
  if (search) {
    queries.push(`search=${search}`);
  }
  if (unitStatuses) {
    unitStatuses.forEach((s, i) => {
      queries.push(`unitStatus[${i}]=${s}`);
    });
  }
  const res = await fetch(`${bookApiUrl}/projects/${projectID}/units/?${queries.join('&')}`, {
    credentials: 'include',
    mode: 'cors',
  });
  const d = await res.json();
  return d || [];
}

export async function getUnitInfo(
  bookApiUrl,
  projectID,
  unitID,
  getRawResponse,
) {
  const res = await fetch(`${bookApiUrl}/projects/${projectID}/units/${unitID}/`, {
    credentials: 'include',
    mode: 'cors',
  });

  if (getRawResponse) {
    return res;
  }
  const d = await res.json();
  return d || {};
}

export async function getUnitBooking(bookApiUrl, unitID) {
  const res = await fetch(`${bookApiUrl}/bookings/?page=1&projectUnitID=${unitID}`, {
    credentials: 'include',
    mode: 'cors',
  });
  const d = await res.json();
  return d;
}

export async function getBookingInfo(bookApiUrl, bookingID) {
  const res = await fetch(`${bookApiUrl}/bookings/${bookingID}/`, {
    credentials: 'include',
    mode: 'cors',
  });
  const d = await res.json();
  return d || {};
}

export async function getBookingList(bookApiUrl) {
  const res = await fetch(`${bookApiUrl}/bookings/`, {
    credentials: 'include',
    mode: 'cors',
  });
  const d = await res.json();
  return d || [];
}

export async function getBookingHistory(bookApiUrl, unitID) {
  const res = await fetch(`${bookApiUrl}/bookings/?section=bookings&projectUnitID=${unitID}`, {
    credentials: 'include',
    mode: 'cors',
  });
  const d = await res.json();
  return d || [];
}

export async function getCancellationReasons(bookApiUrl) {
  if (bookApiUrl) {
    try {
      const res = await fetch(`${bookApiUrl}/cancellation_reasons/`, {
        credentials: 'include',
        mode: 'cors',
      });
      const d = await res.json();
      return d || [];
    } catch (e) {
      return [];
    }
  }
  return [];
}
